/**
 * One thing a customer can put in their tiffin: photo, name, price, and how
 * many go in each box.
 *
 * The photograph comes from the operator, falling back to one of the meal
 * photographs the app ships when the name matches, and to the tiffin glyph
 * otherwise — never an empty grey square.
 */

import { memo } from 'react';
import { Image, StyleSheet, View } from 'react-native';

import { formatMoney, type MenuItem } from '@mfa/domain';
import { colors, radius, shadow, space } from '../../theme';
import { mealImageSource } from '../../assets';
import { Glyph } from '../common/Icon';
import { Text } from '../common/Text';
import { ItemStepper } from './ItemStepper';
import { useLocalized } from '../../core/i18n/content';

const THUMB = 64;

export interface MenuItemCardProps {
  item: MenuItem;
  quantity: number;
  onChange: (quantity: number) => void;
}

export const MenuItemCard = memo(function MenuItemCard({
  item,
  quantity,
  onChange,
}: MenuItemCardProps) {
  const pick = useLocalized();
  const name = pick(item.name, item.nameUr);
  const description = pick(item.description, item.descriptionUr);
  const image = mealImageSource(item.imageUrl, item.name);
  const selected = quantity > 0;

  return (
    <View
      style={[styles.card, selected && styles.cardSelected]}
      testID={`menu-item-${item.id}`}
    >
      {image ? (
        <Image
          source={image}
          style={styles.thumb}
          resizeMode="cover"
          accessibilityIgnoresInvertColors
        />
      ) : (
        <View style={[styles.thumb, styles.placeholder]}>
          <Glyph name="tiffin" size={28} />
        </View>
      )}

      <View style={styles.body}>
        <Text variant="cardTitle" numberOfLines={2}>
          {name}
        </Text>
        {description ? (
          <Text variant="caption" tone="secondary" numberOfLines={2}>
            {description}
          </Text>
        ) : null}
        <View style={styles.footer}>
          <Text variant="bodyStrong" tone="brand">
            {formatMoney(item.price)}
          </Text>
          <ItemStepper
            value={quantity}
            max={item.maxQuantity}
            onChange={onChange}
            name={name}
            testID={`item-${item.id}`}
          />
        </View>
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    gap: space.md,
    padding: space.md,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.bg.surface,
    ...shadow('card'),
  },
  cardSelected: {
    borderColor: colors.border.brand,
    backgroundColor: colors.bg.selected,
  },
  thumb: {
    width: THUMB,
    height: THUMB,
    borderRadius: radius.md,
  },
  placeholder: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.bg.brandSubtle,
  },
  body: {
    flex: 1,
    gap: space.xxs,
    minWidth: 0,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: space.sm,
    marginTop: space.xs,
  },
});
